import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ATTRIBUTES, positionAbbrev, positionColor, formatDateDE, sortPlayersByName } from "@/lib/playerConstants";
import { ageFromGeburtsdatum } from "@/lib/teamBalance";
import { playerStats as trainingStats } from "@/lib/training";
import { playerMatchStats, noteColor } from "@/lib/matchStats";
import { Button } from "@/components/ui/button";
import PlayerSelect from "@/components/PlayerSelect";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend, ResponsiveContainer, Tooltip,
} from "recharts";
import { ArrowLeft, ArrowRightLeft, Star, ArrowUp } from "lucide-react";
import { toast } from "sonner";

const COLOR_A = "#145589";        // brand
const COLOR_A_LIGHT = "#3389c7";  // brand-lighter (visible on dark)
const COLOR_B = "#f59e0b";        // amber-500
const COLOR_B_LIGHT = "#fbbf24";  // amber-400

export default function PlayerCompare() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [players, setPlayers] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aId, setAId] = useState(searchParams.get("a") || "");
  const [bId, setBId] = useState(searchParams.get("b") || "");

  useEffect(() => {
    (async () => {
      try {
        const [p, t, m] = await Promise.all([
          api.get("/players"),
          api.get("/trainings"),
          api.get("/matches"),
        ]);
        setPlayers(p.data);
        setTrainings(t.data);
        setMatches(m.data);
        // Preselect: erste beiden alphabetisch sortierten Spieler, sofern nichts gewählt
        const sorted = sortPlayersByName(p.data);
        if (!aId && sorted[0]) setAId(sorted[0].id);
        if (!bId && sorted[1]) setBId(sorted[1].id);
      } catch {
        toast.error("Daten konnten nicht geladen werden");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const next = new URLSearchParams();
    if (aId) next.set("a", aId);
    if (bId) next.set("b", bId);
    setSearchParams(next, { replace: true });
  }, [aId, bId, setSearchParams]);

  const a = useMemo(() => players.find((p) => p.id === aId) || null, [players, aId]);
  const b = useMemo(() => players.find((p) => p.id === bId) || null, [players, bId]);

  const aTrain = useMemo(() => (a ? trainingStats(a.id, trainings) : null), [a, trainings]);
  const bTrain = useMemo(() => (b ? trainingStats(b.id, trainings) : null), [b, trainings]);
  const aMatch = useMemo(() => (a ? playerMatchStats(a.id, matches) : null), [a, matches]);
  const bMatch = useMemo(() => (b ? playerMatchStats(b.id, matches) : null), [b, matches]);

  const radarData = useMemo(() => {
    if (!a || !b) return [];
    return ATTRIBUTES.map((attr) => ({
      attribute: attr.label,
      short: attr.label.length > 9 ? attr.label.slice(0, 7) + "." : attr.label,
      A: a.attribute[attr.key],
      B: b.attribute[attr.key],
    }));
  }, [a, b]);

  const strengths = useMemo(() => {
    if (!a || !b) return { aStrong: [], bStrong: [] };
    const diffs = ATTRIBUTES.map((attr) => ({
      key: attr.key,
      label: attr.label,
      a: a.attribute[attr.key],
      b: b.attribute[attr.key],
      diff: a.attribute[attr.key] - b.attribute[attr.key],
    }));
    const aStrong = diffs.filter((d) => d.diff > 0).sort((x, y) => y.diff - x.diff).slice(0, 5);
    const bStrong = diffs.filter((d) => d.diff < 0).sort((x, y) => x.diff - y.diff).slice(0, 5);
    return { aStrong, bStrong };
  }, [a, b]);

  const swap = () => { const t = aId; setAId(bId); setBId(t); };

  if (loading) return <div className="text-center py-20 text-zinc-500">Lädt…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <button onClick={() => navigate("/dashboard")}
        className="text-xs text-zinc-400 hover:text-brand-lighter transition flex items-center gap-1 mb-5"
        data-testid="pc-back-btn">
        <ArrowLeft className="h-3 w-3" /> Zurück zum Dashboard
      </button>

      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-brand-lighter mb-1">Analyse</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-100">Spielervergleich</h1>
        <p className="text-zinc-400 text-sm mt-1">Zwei Spieler datenbasiert nebeneinander.</p>
      </div>

      {/* Selectors */}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-center mb-6">
        <PlayerSelect value={aId} onChange={setAId} players={players} disabledId={bId} accent="text-brand-lighter" testId="pc-select-a" />
        <Button variant="outline" onClick={swap} disabled={!a || !b}
          className="bg-transparent border-zinc-700 text-zinc-100 hover:text-brand-lighter hover:bg-zinc-900 rounded-md h-10"
          data-testid="pc-swap-btn">
          <ArrowRightLeft className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Tauschen</span>
        </Button>
        <PlayerSelect value={bId} onChange={setBId} players={players} disabledId={aId} accent="text-amber-400" testId="pc-select-b" />
      </div>

      {!a || !b ? (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-10 text-center text-zinc-500">
          Bitte zwei Spieler auswählen.
        </div>
      ) : (
        <>
          {/* Kennzahlen */}
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden mb-6" data-testid="pc-kennzahlen">
            <KennzahlRow
              label="Name"
              a={<span className="text-brand-lighter font-semibold" data-testid="pc-a-header">{a.name}</span>}
              b={<span className="text-amber-400 font-semibold" data-testid="pc-b-header">{b.name}</span>}
            />
            <KennzahlRow
              label="Position"
              a={<span className={"font-bold " + positionColor(a.position)} data-testid="pc-a-pos">{positionAbbrev(a.position)}</span>}
              b={<span className={"font-bold " + positionColor(b.position)} data-testid="pc-b-pos">{positionAbbrev(b.position)}</span>}
            />
            <KennzahlRow
              label="Geburtstag"
              a={<span className="text-zinc-100">{formatDateDE(a.geburtsdatum)}</span>}
              b={<span className="text-zinc-100">{formatDateDE(b.geburtsdatum)}</span>}
            />
            <KennzahlRow
              label="Gesamtbewertung"
              a={<NumberBox value={a.overall.toFixed(1)} color="text-brand-lighter" testId="pc-a-overall" />}
              b={<NumberBox value={b.overall.toFixed(1)} color="text-amber-400" testId="pc-b-overall" />}
            />
            <KennzahlRow
              label="Talent"
              a={<Stars value={a.talent} color="text-brand-lighter" />}
              b={<Stars value={b.talent} color="text-amber-400" />}
            />
            <KennzahlRow
              label="Anwesenheit"
              a={<NumberBox value={aTrain?.total > 0 ? `${aTrain.quote.toFixed(1)} %` : "—"} color="text-brand-lighter" testId="pc-a-anw" />}
              b={<NumberBox value={bTrain?.total > 0 ? `${bTrain.quote.toFixed(1)} %` : "—"} color="text-amber-400" testId="pc-b-anw" />}
            />
            <KennzahlRow
              label="Ø Note"
              a={<NumberBox value={aMatch?.seasonAvg != null ? aMatch.seasonAvg.toFixed(1) : "—"} color={noteColor(aMatch?.seasonAvg)} testId="pc-a-note" />}
              b={<NumberBox value={bMatch?.seasonAvg != null ? bMatch.seasonAvg.toFixed(1) : "—"} color={noteColor(bMatch?.seasonAvg)} testId="pc-b-note" />}
              last
            />
          </div>

          {/* Radar */}
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4 sm:p-6 mb-6 min-h-[440px]" data-testid="pc-radar">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="text-[11px] uppercase tracking-wider text-zinc-400">Leistungsprofil-Overlay</div>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLOR_A_LIGHT }} /> {a.name}</span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLOR_B }} /> {b.name}</span>
              </div>
            </div>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} outerRadius="75%">
                  <PolarGrid stroke="#27272a" />
                  <PolarAngleAxis dataKey="short" tick={{ fill: "#a1a1aa", fontSize: 11 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: "#52525b", fontSize: 10 }} axisLine={false} />
                  <Tooltip
                    contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 4, fontSize: 12 }}
                    labelStyle={{ color: "#a1a1aa" }}
                  />
                  <Radar name={a.name} dataKey="A" stroke={COLOR_A_LIGHT} fill={COLOR_A_LIGHT} fillOpacity={0.28} strokeWidth={2} />
                  <Radar name={b.name} dataKey="B" stroke={COLOR_B} fill={COLOR_B} fillOpacity={0.22} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Stärkenanalyse */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StrengthCard
              title={a.name}
              subtitle="stärker in"
              accent="border-brand text-brand-lighter"
              dotColor="bg-brand-lighter"
              items={strengths.aStrong.map((s) => ({ label: s.label, diff: s.diff }))}
              testId="pc-strong-a"
            />
            <StrengthCard
              title={b.name}
              subtitle="stärker in"
              accent="border-amber-500/40 text-amber-400"
              dotColor="bg-amber-400"
              items={strengths.bStrong.map((s) => ({ label: s.label, diff: -s.diff }))}
              testId="pc-strong-b"
            />
          </div>
        </>
      )}
    </div>
  );
}

function isBetterNote(a, b) {
  if (a == null && b == null) return false;
  if (a == null) return false;
  if (b == null) return true;
  return a <= b;
}

function PlayerHeader({ p, color, testId }) {
  return (
    <div className="py-3 px-2" data-testid={testId}>
      <div className={"font-display text-base sm:text-lg font-bold truncate " + color} title={p.name}>{p.name}</div>
      <div className={"text-[11px] tracking-wide font-medium mt-0.5 " + positionColor(p.position)}>
        {positionAbbrev(p.position)} · {p.jahrgang}
      </div>
    </div>
  );
}

function KennzahlRow({ label, a, b, last = false }) {
  return (
    <div className={"grid grid-cols-[1fr_2.4fr_1fr] " + (last ? "" : "border-b border-zinc-800")}>
      <div className="py-3 px-2 flex items-center justify-center text-center text-sm">{a}</div>
      <div className="py-3 px-2 border-l border-r border-zinc-800 bg-zinc-950/40 flex items-center justify-center">
        <span className="text-sm text-zinc-400">{label}</span>
      </div>
      <div className="py-3 px-2 flex items-center justify-center text-center text-sm">{b}</div>
    </div>
  );
}

function NumberBox({ value, color, testId }) {
  return (
    <span className={"font-semibold tabular-nums " + color} data-testid={testId}>
      {value}
    </span>
  );
}

function Stars({ value, color }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={"h-3.5 w-3.5 " + (i < value ? "fill-current " + color : "fill-transparent text-zinc-700")} />
      ))}
    </span>
  );
}

function StrengthCard({ title, subtitle, accent, dotColor, items, testId }) {
  return (
    <div className={"rounded-lg border bg-zinc-900/40 p-5 " + accent.split(" ")[0]} data-testid={testId}>
      <div className="flex items-baseline gap-2 mb-3">
        <div className={"font-display text-lg font-bold truncate " + accent.split(" ")[1]} title={title}>{title}</div>
        <div className="text-[11px] uppercase tracking-wide text-zinc-500">{subtitle}</div>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-zinc-500">Keine eindeutigen Vorteile.</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.label} className="flex items-center justify-between py-1.5 border-b border-zinc-800 last:border-b-0">
              <div className="flex items-center gap-2.5">
                <span className={"h-1.5 w-1.5 rounded-full " + dotColor} />
                <span className="text-sm text-zinc-100">{it.label}</span>
              </div>
              <div className="flex items-center gap-1 font-display font-bold tabular-nums">
                <ArrowUp className={"h-3.5 w-3.5 " + accent.split(" ")[1]} />
                <span className={accent.split(" ")[1]}>+{it.diff}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
